/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import {
    Configuration,
    ConfigurationParameters,
    OfferPreviewGetRequest,
    PreviewTagApi,
    ScreenType,
    UnifiedCollectionPreviewGetRequest,
    VodOfferPreviewGetRequest,
} from 'client-code/previews';

import { CommonHeaders } from '../CommonHeaders';
import { Constants } from '../constants';
import { IPreviewsServiceRepository } from '../interfaces';
import { IRecordingPreviewParams } from '../interfaces/IPreviewService';
import { Log } from '../logging';
import {
    TContentPreview,
    TOfferPreview,
    TServicesParams,
    TUnifiedCollectionPreview,
    TUnifiedCollectionPreviewParams,
} from '../types';
import { UniqueIdentifier } from '../utils';

export class PreviewsServiceRepositoryImpl implements IPreviewsServiceRepository {
    private static TAG: string = 'PreviewsServiceRepositoryImpl';

    private mParams: TServicesParams;

    public constructor(params: TServicesParams) {
        this.mParams = params;
    }

    private getPreviewApi(commonHeaders: CommonHeaders['headers']): PreviewTagApi {
        const configurationParameters = this.getConfigurationParameters(commonHeaders);
        const configuration = new Configuration(configurationParameters);
        return new PreviewTagApi(configuration);
    }

    private getConfigurationParameters(
        commonHeaders: CommonHeaders['headers'],
    ): ConfigurationParameters {
        return {
            apiKey: this.mParams.accessToken,
            basePath: this.mParams.basePath,
            baseOptions: {
                timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                headers: commonHeaders,
            },
        };
    }

    private getRequestParameters(commonHeaders: CommonHeaders['headers']) {
        const {
            ApplicationName,
            DeviceType,
            ProductName,
            ApplicationFeatureArea,
            ApplicationVersion,
        } = commonHeaders;
        return {
            timeZoneName: this.mParams.timeZoneName,
            applicationName: ApplicationName,
            applicationFeatureArea: ApplicationFeatureArea,
            applicationVersion: ApplicationVersion,
            bodyId: this.mParams.bodyId,
            deviceType: DeviceType,
            productName: ProductName,
            acceptLanguage: commonHeaders['Accept-Language'],
            xAmznRequestId: UniqueIdentifier.generateXAmznRequestId(),
            whiny: this.mParams.whiny || 'false',
        };
    }

    private async getOfferPreviewFromService(
        offerId: string,
        screenType: ScreenType,
        networkCallId?: number,
    ): Promise<TOfferPreview> {
        try {
            const commonHeaders = new CommonHeaders(screenType).headers;
            const offerPreviewRequestParameters: OfferPreviewGetRequest = {
                offerId,
                screenType,
                ...this.getRequestParameters(commonHeaders),
            };

            // screenType is marked as optional in schema but server needs it
            const previewsOfferResponse = await this.getPreviewApi(commonHeaders).offerPreviewGet(
                offerPreviewRequestParameters,
            );

            const offerPreviewResponse: TOfferPreview = {
                offerPreview: previewsOfferResponse.data || {},
                networkCallId,
            };

            return offerPreviewResponse;
        } catch (error: any) {
            error.networkCallId = networkCallId;
            Log.e(PreviewsServiceRepositoryImpl.TAG, 'getOfferPreviewFromService()', error);
            throw error;
        }
    }

    private async getUnifiedCollectionPreviewFromService(
        unifiedCollectionPreviewParams: TUnifiedCollectionPreviewParams,
    ): Promise<TUnifiedCollectionPreview> {
        try {
            const commonHeaders = new CommonHeaders(ScreenType.SeriesScreen).headers;
            const { unifiedCollectionId, contentSupplierPartnerId, groups, partnerId } =
                unifiedCollectionPreviewParams;
            const unifiedCollectionPreviewRequestParameters: UnifiedCollectionPreviewGetRequest = {
                unifiedCollectionId,
                contentSupplierPartnerId,
                groups,
                partnerId,
                ...this.getRequestParameters(commonHeaders),
            };

            const unifiedCollectionPreviewResponse = await this.getPreviewApi(
                commonHeaders,
            ).unifiedCollectionPreviewGet(unifiedCollectionPreviewRequestParameters);

            return unifiedCollectionPreviewResponse.data;
        } catch (error: any) {
            Log.e(
                PreviewsServiceRepositoryImpl.TAG,
                'getUnifiedCollectionPreviewFromService()',
                error,
            );
            throw error;
        }
    }

    private async getVodOfferPreviewFromService(
        vodOfferId: string,
        screenType: ScreenType,
        networkCallId?: number,
    ): Promise<TOfferPreview> {
        try {
            const commonHeaders = new CommonHeaders(screenType).headers;
            const vodOfferPreviewRequestParameters: VodOfferPreviewGetRequest = {
                screenType,
                vodOfferId,
                ...this.getRequestParameters(commonHeaders),
            };

            // screenType is marked as optional in schema but server needs it
            const previewsVodOfferResponse = await this.getPreviewApi(
                commonHeaders,
            ).vodOfferPreviewGet(vodOfferPreviewRequestParameters);
            const vodOfferPreviewResponse: TOfferPreview = {
                offerPreview: previewsVodOfferResponse.data || {},
                networkCallId,
            };
            return vodOfferPreviewResponse;
        } catch (error: any) {
            error.networkCallId = networkCallId;
            Log.e(PreviewsServiceRepositoryImpl.TAG, 'getVodOfferPreviewFromService()', error);
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public fetchContentPreview(contentId: string): Promise<TContentPreview> {
        throw new Error('Method not implemented.');
    }

    /**
     * @inheritdoc
     */
    public async fetchOfferPreview(
        offerId: string,
        screenType: ScreenType,
        networkCallId?: number,
    ): Promise<TOfferPreview> {
        try {
            return await this.getOfferPreviewFromService(offerId, screenType, networkCallId);
        } catch (error) {
            Log.e(PreviewsServiceRepositoryImpl.TAG, 'error', error);
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async fetchRecordingPreview(
        recordingPreviewParams: IRecordingPreviewParams,
    ): Promise<TOfferPreview> {
        throw new Error('Method not implemented.');
    }

    /**
     * @inheritdoc
     */
    public async fetchUnifiedCollectionPreview(
        unifiedCollectionPreviewParams: TUnifiedCollectionPreviewParams,
    ): Promise<TUnifiedCollectionPreview> {
        try {
            return await this.getUnifiedCollectionPreviewFromService(
                unifiedCollectionPreviewParams,
            );
        } catch (error) {
            Log.e(PreviewsServiceRepositoryImpl.TAG, 'error', error);
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async fetchVodOfferPreview(
        vodOfferId: string,
        screenType: ScreenType,
        networkCallId?: number,
    ): Promise<TOfferPreview> {
        try {
            return await this.getVodOfferPreviewFromService(vodOfferId, screenType, networkCallId);
        } catch (error) {
            Log.e(PreviewsServiceRepositoryImpl.TAG, 'error', error);
            throw error;
        }
    }
}
